import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
	fetchCashFlowStatementTTM,
	fetchIncomeStatementTTM,
	fetchKeyMetricsTTM,
	fetchStockQuote,
} from '../../services/stockApi';
import {
	fetchFiscalStandardizedCashFlowStatement,
	fetchFiscalStandardizedCashFlowStatementTtm,
	fetchFiscalStandardizedIncomeStatement,
	fetchFiscalStandardizedIncomeStatementTtm,
} from '../../services/fiscalApi';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import type { SnackbarVariant } from '../../components/Snackbar';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

interface ResearchValuationScreenProps {
	isDarkMode: boolean;
	selectedStock: string;
	authToken?: string | null;
	authUserId?: number | null;
	onUnauthorized?: () => void;
	onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

type SideInputs = {
	base: string;
	growthRate: string;
	multiple: string;
	years: string;
	minimumReturn: string;
};

type ValuationInputs = {
	eps: SideInputs;
	cashFlow: SideInputs;
};

type SideResult = {
	futurePrice: number;
	expectedCagrPct: number;
	buyPrice: number;
	years: number;
	minimumReturn: number;
};

const DEFAULT_YEARS = '6';
const DEFAULT_MIN_RETURN = '12';

const fmtInput2 = (value: number | null): string => {
	if (value == null || !Number.isFinite(value)) return '';
	return value.toFixed(2);
};

const buildDefaultInputs = (reportEps: number | null, reportOcfPerShare: number | null): ValuationInputs => ({
	eps: {
		base: fmtInput2(reportEps),
		growthRate: '',
		multiple: '',
		years: DEFAULT_YEARS,
		minimumReturn: DEFAULT_MIN_RETURN,
	},
	cashFlow: {
		base: fmtInput2(reportOcfPerShare),
		growthRate: '',
		multiple: '',
		years: DEFAULT_YEARS,
		minimumReturn: DEFAULT_MIN_RETURN,
	},
});

const toNum = (v: string): number | null => {
	const parsed = Number(v);
	return Number.isFinite(parsed) ? parsed : null;
};

const toFiniteNumber = (value: unknown): number | null => {
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

const toFixed2 = (value: number | null): number | null => {
	if (value == null || !Number.isFinite(value)) return null;
	return Math.round(value * 100) / 100;
};

const resolveOperatingCashFlowPerShare = (row: unknown): number | null => {
	if (!row || typeof row !== 'object') return null;
	const r = row as Record<string, unknown>;
	const candidates = [
		r.operatingCashFlowPerShare,
		r.operatingCashFlowPerShareTTM,
		r.operatingCashFlowPerShareTtm,
		r.ocfPerShare,
		r.ocfPerShareTTM,
		r.ocfPerShareTtm,
	];

	for (const value of candidates) {
		const parsed = toFiniteNumber(value);
		if (parsed != null) return parsed;
	}

	return null;
};

const resolveOperatingCashFlowPerShareFromTtm = (keyMetricsRow: unknown, cashFlowRow: unknown, incomeRow: unknown): number | null => {
	const fromKeyMetrics = resolveOperatingCashFlowPerShare(keyMetricsRow);
	if (fromKeyMetrics != null) return fromKeyMetrics;

	const cf = (cashFlowRow && typeof cashFlowRow === 'object') ? (cashFlowRow as Record<string, unknown>) : null;
	const income = (incomeRow && typeof incomeRow === 'object') ? (incomeRow as Record<string, unknown>) : null;
	if (!cf || !income) return null;

	const operatingCashFlow =
		toFiniteNumber(cf.operatingCashFlow) ??
		toFiniteNumber(cf.netCashProvidedByOperatingActivities) ??
		null;

	const shares =
		toFiniteNumber(income.weightedAverageShsOutDil) ??
		toFiniteNumber(income.weightedAverageShsOut) ??
		null;

	if (operatingCashFlow == null || shares == null || shares <= 0) return null;
	return operatingCashFlow / shares;
};

const fmtUsd = (value: number | null | undefined): string => {
	if (value == null || !Number.isFinite(value)) return 'Outcome ($)';
	return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtPct = (value: number | null | undefined): string => {
	if (value == null || !Number.isFinite(value)) return 'Outcome (%)';
	return `${value.toFixed(2)}%`;
};

const fmtReturnLabel = (value: string): string => {
	const parsed = toNum(value);
	const safe = parsed != null ? parsed : Number(DEFAULT_MIN_RETURN);
	if (!Number.isFinite(safe)) return DEFAULT_MIN_RETURN;
	if (Math.abs(safe - Math.round(safe)) < 0.000001) return String(Math.round(safe));
	return safe.toFixed(2).replace(/\.00$/, '').replace(/(\.\d*[1-9])0$/, '$1');
};

const roundYAxisMax = (maxValue: number): number => {
	if (!Number.isFinite(maxValue) || maxValue <= 0) return 100;
	const padded = maxValue * 1.1;
	if (padded <= 200) return Math.ceil(padded / 20) * 20;
	if (padded <= 1000) return Math.ceil(padded / 50) * 50;
	if (padded <= 5000) return Math.ceil(padded / 100) * 100;
	if (padded <= 10000) return Math.ceil(padded / 200) * 200;
	return Math.ceil(padded / 500) * 500;
};

const yAxisStepForMax = (maxValue: number): number => {
	if (maxValue <= 200) return 20;
	if (maxValue <= 1000) return 50;
	if (maxValue <= 5000) return 100;
	if (maxValue <= 10000) return 200;
	return 500;
};

const storageKey = (symbol: string) => `valuation_inputs_v2_${String(symbol || '').trim().toUpperCase()}`;

const hasMeaningfulInputs = (side: SideInputs): boolean => {
	return [side.base, side.growthRate, side.multiple, side.years, side.minimumReturn]
		.some((value) => String(value ?? '').trim().length > 0);
};

const hasSavedValuationState = (state: ValuationInputs, createdAtUtc?: string | null, updatedAtUtc?: string | null): boolean => {
	return Boolean(createdAtUtc || updatedAtUtc || hasMeaningfulInputs(state.eps) || hasMeaningfulInputs(state.cashFlow));
};

const calcSideResult = (inputs: SideInputs, currentPrice: number | null): SideResult | null => {
	const base = toNum(inputs.base);
	const growthRate = toNum(inputs.growthRate);
	const multiple = toNum(inputs.multiple);
	const yearsRaw = toNum(inputs.years);

	if (base == null || growthRate == null || multiple == null || yearsRaw == null) return null;
	if (base <= 0 || multiple <= 0 || yearsRaw <= 0) return null;

	const years = Math.max(1, Math.round(yearsRaw));
	const minimumReturnParsed = toNum(inputs.minimumReturn);
	const minimumReturn = minimumReturnParsed != null ? minimumReturnParsed : 12;

	const futurePrice = base * Math.pow(1 + growthRate / 100, years) * multiple;
	const buyPrice = futurePrice / Math.pow(1 + minimumReturn / 100, years);

	let expectedCagrPct = NaN;
	if (currentPrice != null && Number.isFinite(currentPrice) && currentPrice > 0) {
		expectedCagrPct = (Math.pow(futurePrice / currentPrice, 1 / years) - 1) * 100;
	}

	return {
		futurePrice,
		expectedCagrPct,
		buyPrice,
		years,
		minimumReturn,
	};
};

const VALUATION_SPINNER_STYLE_ID = 'valuation-no-spinner-style';
if (typeof document !== 'undefined' && !document.getElementById(VALUATION_SPINNER_STYLE_ID)) {
	const s = document.createElement('style');
	s.id = VALUATION_SPINNER_STYLE_ID;
	s.textContent = '#valuation-screen input[type=number]::-webkit-outer-spin-button,' +
		'#valuation-screen input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}' +
		'#valuation-screen button[id$="-minus"]:focus,' +
		'#valuation-screen button[id$="-minus"]:focus-visible,' +
		'#valuation-screen button[id$="-plus"]:focus,' +
		'#valuation-screen button[id$="-plus"]:focus-visible{outline:none;box-shadow:none;}';
	document.head.appendChild(s);
}

const ResearchValuationScreen: React.FC<ResearchValuationScreenProps> = ({
	isDarkMode,
	selectedStock,
	authToken,
	onUnauthorized,
	onNotify,
}) => {
	const symbol = useMemo(() => String(selectedStock || '').trim().toUpperCase(), [selectedStock]);

	const colors = useMemo(() => {
		const dark = isDarkMode;
		return {
			pageBg: dark ? '#0b1220' : '#f3f4f6',
			text: dark ? '#e5e7eb' : '#0f172a',
			subText: dark ? '#94a3b8' : '#5f6572',
			cardBg: dark ? '#0f172a' : '#ffffff',
			border: dark ? '#374151' : '#d9dde3',
			headerBg: dark ? '#111827' : '#f8fafc',
			inputBg: dark ? '#111827' : '#ffffff',
			inputBorder: dark ? '#334155' : '#d3d7de',
			mutedBtnText: dark ? '#9ca3af' : '#3366cc',
			outcomeBg: dark ? '#111827' : '#eceff3',
			outcomeBorder: dark ? '#334155' : '#c3c8d0',
			blue: '#3B82F6',
			teal: '#0ea5a8',
			red: '#dc2626',
			chartGrid: dark ? '#1f2937' : '#edf2f7',
		};
	}, [isDarkMode]);

	const [reportEps, setReportEps] = useState<number | null>(null);
	const [reportOcfPerShare, setReportOcfPerShare] = useState<number | null>(null);
	const [currentPrice, setCurrentPrice] = useState<number | null>(null);
	const [valuationDataSource, setValuationDataSource] = useState<'fiscal' | 'fmp'>('fmp');
	const [inputs, setInputs] = useState<ValuationInputs>(() => buildDefaultInputs(null, null));
	const [hasLoadedInitialState, setHasLoadedInitialState] = useState(false);
	const [activeControlKey, setActiveControlKey] = useState<string | null>(null);
	const hydratedSymbolRef = useRef<string | null>(null);
	const [userModifiedEpsBase, setUserModifiedEpsBase] = useState(false);
	const [userModifiedCashFlowBase, setUserModifiedCashFlowBase] = useState(false);
	const [hasStoredCashFlowBase, setHasStoredCashFlowBase] = useState(false);
	const [savingBookmarkByPanel, setSavingBookmarkByPanel] = useState<Record<string, boolean>>({});
	const [bookmarkFlashByPanel, setBookmarkFlashByPanel] = useState<Record<string, boolean>>({});
	const bookmarkFlashTimeoutsRef = useRef<Record<string, number>>({});
	const [expandedChartId, setExpandedChartId] = useState<string | null>(null);

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			hydratedSymbolRef.current = null;
			setUserModifiedEpsBase(false);
			setUserModifiedCashFlowBase(false);
			setHasLoadedInitialState(false);
			setReportEps(null);
			setReportOcfPerShare(null);
			setCurrentPrice(null);
			setValuationDataSource('fmp');
			setInputs(buildDefaultInputs(null, null));
			setHasStoredCashFlowBase(false);

			let didHydrate = false;
			try {
				const [incomeTtmResult, keyMetricsResult, cashFlowTtmResult, quoteResult, fiscalIncomeStatementResult, fiscalCashFlowStatementResult, fiscalIncomeTtmResult, fiscalCashFlowTtmResult] = await Promise.allSettled([
					fetchIncomeStatementTTM(symbol),
					fetchKeyMetricsTTM(symbol),
					fetchCashFlowStatementTTM(symbol),
					fetchStockQuote(symbol),
					fetchFiscalStandardizedIncomeStatement(symbol),
					fetchFiscalStandardizedCashFlowStatement(symbol),
					fetchFiscalStandardizedIncomeStatementTtm(symbol),
					fetchFiscalStandardizedCashFlowStatementTtm(symbol),
				]);

				if (!mounted) return;

				const incomeTtm = incomeTtmResult.status === 'fulfilled' ? incomeTtmResult.value : null;
				const keyMetricsTtm = keyMetricsResult.status === 'fulfilled' ? keyMetricsResult.value : null;
				const cashFlowTtm = cashFlowTtmResult.status === 'fulfilled' ? cashFlowTtmResult.value : null;
				const quote = quoteResult.status === 'fulfilled' ? quoteResult.value : null;
				const fiscalIncomeStatement = fiscalIncomeStatementResult.status === 'fulfilled' ? fiscalIncomeStatementResult.value : null;
				const fiscalCashFlowStatement = fiscalCashFlowStatementResult.status === 'fulfilled' ? fiscalCashFlowStatementResult.value : null;
				const fiscalIncomeTtm = fiscalIncomeTtmResult.status === 'fulfilled' ? fiscalIncomeTtmResult.value.row : null;
				const fiscalCashFlowTtm = fiscalCashFlowTtmResult.status === 'fulfilled' ? fiscalCashFlowTtmResult.value.row : null;

				const fiscalEps = fiscalIncomeTtm?.epsDiluted ?? fiscalIncomeTtm?.eps ?? null;
				const fiscalOperatingCashFlow = fiscalCashFlowTtm?.operatingCashFlow ?? fiscalCashFlowTtm?.netCashProvidedByOperatingActivities ?? null;
				const fiscalShares = fiscalIncomeTtm?.dilutedWeightedAverageSharesOutstanding ?? null;
				const fiscalOcfPerShare = fiscalOperatingCashFlow != null && fiscalShares != null && fiscalShares > 0
					? fiscalOperatingCashFlow / fiscalShares
					: null;
				const fiscalErrorText = String(
					fiscalIncomeStatement?.error ||
					fiscalCashFlowStatement?.error ||
					(fiscalIncomeTtmResult.status === 'fulfilled' ? fiscalIncomeTtmResult.value.error : '') ||
					(fiscalCashFlowTtmResult.status === 'fulfilled' ? fiscalCashFlowTtmResult.value.error : '') ||
					''
				).trim();
				const fiscalDataCount =
					(fiscalIncomeStatement?.annual.length ?? 0) +
					(fiscalIncomeStatement?.quarter.length ?? 0) +
					(fiscalCashFlowStatement?.annual.length ?? 0) +
					(fiscalCashFlowStatement?.quarter.length ?? 0);
				const fiscalUnavailableByMessage = Boolean(fiscalErrorText) && /data is not yet available|companies-list|companies list/i.test(fiscalErrorText);
				const fiscalAvailable = !fiscalUnavailableByMessage && fiscalDataCount > 0;
				const hasFiscalValuationInputs = fiscalAvailable && Number.isFinite(Number(fiscalEps)) && Number.isFinite(Number(fiscalOcfPerShare));
				const nextReportEps = hasFiscalValuationInputs
					? Number(fiscalEps)
					: (incomeTtm?.epsDiluted ?? incomeTtm?.eps ?? null);
				const nextReportOcf = hasFiscalValuationInputs
					? Number(fiscalOcfPerShare)
					: resolveOperatingCashFlowPerShareFromTtm(keyMetricsTtm, cashFlowTtm, incomeTtm);
				const nextCurrentPrice = quote?.price ?? null;

				setValuationDataSource(hasFiscalValuationInputs ? 'fiscal' : 'fmp');
				setReportEps(nextReportEps != null && Number.isFinite(nextReportEps) ? nextReportEps : null);
				setReportOcfPerShare(nextReportOcf != null && Number.isFinite(nextReportOcf) ? nextReportOcf : null);
				setCurrentPrice(nextCurrentPrice != null && Number.isFinite(nextCurrentPrice) ? nextCurrentPrice : null);

				let stored: ValuationInputs | null = null;
				let nextHasStoredValuation = false;

				if (authToken) {
					try {
						const response = await adminApi.getMyValuationState(authToken, symbol);
						if (!mounted) return;
						const apiStored: ValuationInputs = {
							eps: {
								base: response.eps.base,
								growthRate: response.eps.growthRate,
								multiple: response.eps.multiple,
								years: response.eps.years,
								minimumReturn: response.eps.minimumReturn,
							},
							cashFlow: {
								base: response.cashFlow.base,
								growthRate: response.cashFlow.growthRate,
								multiple: response.cashFlow.multiple,
								years: response.cashFlow.years,
								minimumReturn: response.cashFlow.minimumReturn,
							},
						};
						nextHasStoredValuation = hasSavedValuationState(apiStored, response.createdAtUtc, response.updatedAtUtc);
						stored = nextHasStoredValuation ? apiStored : null;
					} catch (error) {
						if (!mounted) return;
						if (isUnauthorized(error)) {
							onUnauthorized?.();
							return;
						}
						onNotify?.('Failed loading saved valuation. Using local fallback.', 'error');
					}
				}

				if (!stored && !authToken) {
					try {
						const raw = localStorage.getItem(storageKey(symbol));
						stored = raw ? (JSON.parse(raw) as ValuationInputs) : null;
						nextHasStoredValuation = Boolean(stored);
					} catch {
						stored = null;
					}
				}

				if (!mounted) return;

				if (stored) {
					const patched = { ...stored };
					let storedEpsBase = toNum(stored.eps.base);
					let storedCashFlowBase = toNum(stored.cashFlow.base);

					// Check if stored base values differ from API values
					const epsModified = storedEpsBase != null && nextReportEps != null &&
						Math.abs(storedEpsBase - nextReportEps) > 0.000001;
					const cashFlowModified = (() => {
						if (storedCashFlowBase == null || nextReportOcf == null) return false;
						const storedRounded = toFixed2(storedCashFlowBase);
						const reportRounded = toFixed2(nextReportOcf);
						if (storedRounded == null || reportRounded == null) return false;
						return Math.abs(storedRounded - reportRounded) > 0.000001;
					})();
					if ((!patched.eps.base || patched.eps.base === '') && nextReportEps != null && Number.isFinite(nextReportEps)) {
						patched.eps = { ...patched.eps, base: fmtInput2(nextReportEps) };
					}
					if ((!patched.cashFlow.base || patched.cashFlow.base === '') && nextReportOcf != null && Number.isFinite(nextReportOcf)) {
						patched.cashFlow = { ...patched.cashFlow, base: fmtInput2(nextReportOcf) };
					}
					setInputs(patched);
					setHasStoredCashFlowBase(cashFlowModified);

					// Set modification flags if stored values differ from API values
					if (epsModified) setUserModifiedEpsBase(true);
					if (cashFlowModified) setUserModifiedCashFlowBase(true);
				} else {
					setInputs(buildDefaultInputs(nextReportEps, nextReportOcf));
					setHasStoredCashFlowBase(false);
				}

				didHydrate = true;
			} finally {
				if (mounted) {
					hydratedSymbolRef.current = didHydrate ? symbol : null;
					setHasLoadedInitialState(didHydrate);
				}
			}
		};

		if (symbol) {
			void load();
		}

		return () => {
			mounted = false;
		};
	}, [authToken, onNotify, onUnauthorized, symbol]);

	useEffect(() => {
		if (!symbol) return;
		if (!hasLoadedInitialState) return;
		if (hydratedSymbolRef.current !== symbol) return;

		if (!authToken) {
			try {
				localStorage.setItem(storageKey(symbol), JSON.stringify(inputs));
			} catch {
				// ignore localStorage errors
			}
			return;
		}

		let cancelled = false;
		const save = async () => {
			try {
				await adminApi.upsertMyValuationState(authToken, {
					stockSymbol: symbol,
					eps: inputs.eps,
					cashFlow: inputs.cashFlow,
				});
			} catch (error) {
				if (cancelled) return;
				if (isUnauthorized(error)) {
					onUnauthorized?.();
					return;
				}
				onNotify?.('Failed saving valuation changes.', 'error');
			}
		};

		void save();

		return () => {
			cancelled = true;
		};
	}, [authToken, hasLoadedInitialState, inputs, onNotify, onUnauthorized, symbol]);

	useEffect(() => {
		return () => {
			const timeouts = bookmarkFlashTimeoutsRef.current;
			Object.keys(timeouts).forEach((key) => {
				try {
					window.clearTimeout(timeouts[key]);
				} catch {
					// ignore
				}
			});
		};
	}, []);

	const triggerBookmarkFlash = (panelId: string) => {
		setBookmarkFlashByPanel((prev) => ({ ...prev, [panelId]: true }));
		const timeouts = bookmarkFlashTimeoutsRef.current;
		if (timeouts[panelId]) {
			try {
				window.clearTimeout(timeouts[panelId]);
			} catch {
				// ignore
			}
		}
		bookmarkFlashTimeoutsRef.current[panelId] = window.setTimeout(() => {
			setBookmarkFlashByPanel((prev) => {
				if (!prev[panelId]) return prev;
				return { ...prev, [panelId]: false };
			});
		}, 30_000);
	};

	const saveValuationBookmark = async (panelId: string, panelTitle: string) => {
		if (!authToken) {
			onNotify?.('Please login to save screenshots.', 'error');
			return;
		}
		if (savingBookmarkByPanel[panelId]) return;
		if (bookmarkFlashByPanel[panelId]) return;

		setSavingBookmarkByPanel((prev) => ({ ...prev, [panelId]: true }));
		try {
			// Capture only the specific panel
			const panelElement = document.getElementById(panelId);
			if (!panelElement) {
				onNotify?.(`${panelTitle} panel is not ready to capture yet.`, 'error');
				setSavingBookmarkByPanel((prev) => ({ ...prev, [panelId]: false }));
				return;
			}

			const canvas = await html2canvas(panelElement, {
				backgroundColor: '#ffffff',
				logging: false,
				useCORS: true,
				allowTaint: true,
			});

			if (!canvas) {
				onNotify?.('Failed capturing screenshot.', 'error');
				setSavingBookmarkByPanel((prev) => ({ ...prev, [panelId]: false }));
				return;
			}

			// Helper to create data URL with fallback
			const toDataUrlWithFallback = (c: HTMLCanvasElement, type: string, quality: number) => {
				try {
					const dataUrl = c.toDataURL(type, quality);
					if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) return { type, dataUrl };
				} catch {
					// ignore
				}
				const fallbackType = 'image/png';
				return { type: fallbackType, dataUrl: c.toDataURL(fallbackType) };
			};

			// Create thumbnail
			const srcW = canvas.width;
			const srcH = canvas.height;
			let thumbDataUrl = toDataUrlWithFallback(canvas, 'image/webp', 0.82).dataUrl;

			if (srcW && srcH) {
				const scale = Math.min(1, 1100 / srcW);
				const dstW = Math.max(1, Math.floor(srcW * scale));
				const dstH = Math.max(1, Math.floor(srcH * scale));

				const thumb = document.createElement('canvas');
				thumb.width = dstW;
				thumb.height = dstH;
				const ctx = thumb.getContext('2d');
				if (ctx) {
					ctx.drawImage(canvas, 0, 0, dstW, dstH);
					thumbDataUrl = toDataUrlWithFallback(thumb, 'image/webp', 0.82).dataUrl;
				}
			}

			const full = toDataUrlWithFallback(canvas, 'image/webp', 0.92);

			await adminApi.createResearchDeskScreenshot(authToken, {
				userId: 0,
				sourcePage: 'Valuation',
				stockSymbol: symbol,
				title: `${panelTitle} Analysis`,
				imageDataUrl: full.dataUrl,
				thumbDataUrl,
			});

			triggerBookmarkFlash(panelId);
			void `${symbol} • ${panelTitle} Analysis • Screenshot saved`;
		} catch (e: any) {
			if (isUnauthorized(e)) {
				onUnauthorized?.();
				return;
			}
			onNotify?.(e?.message || 'Failed saving screenshot', 'error');
		} finally {
			setSavingBookmarkByPanel((prev) => ({ ...prev, [panelId]: false }));
		}
	};
	const epsResult = useMemo(() => calcSideResult(inputs.eps, currentPrice), [inputs.eps, currentPrice]);
	const cashFlowResult = useMemo(() => calcSideResult(inputs.cashFlow, currentPrice), [inputs.cashFlow, currentPrice]);
	const epsReturnLabel = useMemo(() => fmtReturnLabel(inputs.eps.minimumReturn), [inputs.eps.minimumReturn]);
	const cashFlowReturnLabel = useMemo(() => fmtReturnLabel(inputs.cashFlow.minimumReturn), [inputs.cashFlow.minimumReturn]);

	const epsNeedsUpdateHighlight = useMemo(() => {
		if (!userModifiedEpsBase) return false;
		const entered = toNum(inputs.eps.base);
		if (entered == null || reportEps == null) return false;
		return Math.abs(entered - reportEps) > 0.000001;
	}, [inputs.eps.base, reportEps, userModifiedEpsBase]);

	const cashFlowNeedsUpdateHighlight = useMemo(() => {
		if (!hasStoredCashFlowBase && !userModifiedCashFlowBase) return false;
		const entered = toNum(inputs.cashFlow.base);
		if (entered == null || reportOcfPerShare == null) return false;
		const enteredRounded = toFixed2(entered);
		const reportRounded = toFixed2(reportOcfPerShare);
		if (enteredRounded == null || reportRounded == null) return false;
		return Math.abs(enteredRounded - reportRounded) > 0.000001;
	}, [hasStoredCashFlowBase, inputs.cashFlow.base, reportOcfPerShare, userModifiedCashFlowBase]);

	const clearEarnings = async () => {
		let nextReportEps = reportEps;

		if (symbol) {
			let refreshedEps: number | null = null;
			if (valuationDataSource === 'fiscal') {
				const incomeTtmResult = await fetchFiscalStandardizedIncomeStatementTtm(symbol).catch(() => null);
				refreshedEps = incomeTtmResult?.row?.epsDiluted ?? incomeTtmResult?.row?.eps ?? null;
			} else {
				const incomeTtmResult = await fetchIncomeStatementTTM(symbol).catch(() => null);
				refreshedEps = incomeTtmResult?.epsDiluted ?? incomeTtmResult?.eps ?? null;
			}
			if (refreshedEps != null) {
				nextReportEps = refreshedEps != null && Number.isFinite(refreshedEps) ? refreshedEps : null;
			}
		}

		setReportEps(nextReportEps);
		setInputs((prev) => ({
			...prev,
			eps: buildDefaultInputs(nextReportEps, reportOcfPerShare).eps,
		}));
		setUserModifiedEpsBase(false);
	};

	const clearCashFlow = async () => {
		let nextReportOcf = reportOcfPerShare;

		if (symbol) {
			let refreshedOcf: number | null = null;
			if (valuationDataSource === 'fiscal') {
				const [incomeTtmResult, cashFlowTtmResult] = await Promise.allSettled([
					fetchFiscalStandardizedIncomeStatementTtm(symbol),
					fetchFiscalStandardizedCashFlowStatementTtm(symbol),
				]);
				const incomeTtm = incomeTtmResult.status === 'fulfilled' ? incomeTtmResult.value.row : null;
				const cashFlowTtm = cashFlowTtmResult.status === 'fulfilled' ? cashFlowTtmResult.value.row : null;
				const operatingCashFlow = cashFlowTtm?.operatingCashFlow ?? cashFlowTtm?.netCashProvidedByOperatingActivities ?? null;
				const shares = incomeTtm?.dilutedWeightedAverageSharesOutstanding ?? null;
				refreshedOcf = operatingCashFlow != null && shares != null && shares > 0 ? operatingCashFlow / shares : null;
			} else {
				const [incomeTtmResult, keyMetricsResult, cashFlowTtmResult] = await Promise.allSettled([
					fetchIncomeStatementTTM(symbol),
					fetchKeyMetricsTTM(symbol),
					fetchCashFlowStatementTTM(symbol),
				]);
				const incomeTtm = incomeTtmResult.status === 'fulfilled' ? incomeTtmResult.value : null;
				const keyMetricsTtm = keyMetricsResult.status === 'fulfilled' ? keyMetricsResult.value : null;
				const cashFlowTtm = cashFlowTtmResult.status === 'fulfilled' ? cashFlowTtmResult.value : null;
				refreshedOcf = resolveOperatingCashFlowPerShareFromTtm(keyMetricsTtm, cashFlowTtm, incomeTtm);
			}
			nextReportOcf = refreshedOcf != null && Number.isFinite(refreshedOcf) ? refreshedOcf : null;
		}

		setReportOcfPerShare(nextReportOcf);
		setInputs((prev) => ({
			...prev,
			cashFlow: buildDefaultInputs(reportEps, nextReportOcf).cashFlow,
		}));
		setHasStoredCashFlowBase(false);
		setUserModifiedCashFlowBase(false);
	};

	const setSideField = (side: 'eps' | 'cashFlow', field: keyof SideInputs, value: string) => {
		setInputs((prev) => ({
			...prev,
			[side]: {
				...prev[side],
				[field]: value,
			},
		}));

		if (side === 'eps' && field === 'base') setUserModifiedEpsBase(true);
		if (side === 'cashFlow' && field === 'base') {
			setHasStoredCashFlowBase(false);
			setUserModifiedCashFlowBase(true);
		}
	};

	const stepField = (side: 'eps' | 'cashFlow', field: keyof SideInputs, delta: number) => {
		const current = toNum(inputs[side][field]);
		const next = (current ?? 0) + delta;
		setSideField(side, field, Number.isFinite(next) ? next.toFixed(2) : String(next));
	};

	const chartData = useMemo(() => {
		const years = Math.max(epsResult?.years ?? 0, cashFlowResult?.years ?? 0, 1);
		const now = new Date();
		const startYear = now.getFullYear();
		const startMonth = now.getMonth() + 1;
		const startMonthPadded = String(startMonth).padStart(2, '0');
		const labels = Array.from({ length: years + 1 }, (_, i) => `${startMonthPadded}-${startYear + i}`);

		const makeSeries = (start: number, end: number, periodYears: number): Array<number | null> => {
			const y = Math.max(1, periodYears);
			const points = Array.from({ length: y + 1 }, (_, i) => start + ((end - start) * i) / y);
			if (points.length < labels.length) {
				return [...points, ...Array(labels.length - points.length).fill(null)];
			}
			return points.slice(0, labels.length);
		};

		const datasets: any[] = [];
		if (epsResult && currentPrice != null && currentPrice > 0) {
			datasets.push({
				label: 'Earnings: Expected CAGR from Today\'s Price',
				data: makeSeries(currentPrice, epsResult.futurePrice, epsResult.years),
				borderColor: colors.blue,
				backgroundColor: colors.blue,
				borderWidth: 2,
				pointRadius: 3,
				tension: 0,
			});
			datasets.push({
				label: `Earnings: Buy Price for ${epsReturnLabel}% Return`,
				data: makeSeries(epsResult.buyPrice, epsResult.futurePrice, epsResult.years),
				borderColor: colors.blue,
				backgroundColor: colors.blue,
				borderWidth: 2,
				borderDash: [6, 6],
				pointRadius: 3,
				tension: 0,
			});
		}

		if (cashFlowResult && currentPrice != null && currentPrice > 0) {
			datasets.push({
				label: 'Cash Flow: Current Price',
				data: makeSeries(currentPrice, cashFlowResult.futurePrice, cashFlowResult.years),
				borderColor: colors.teal,
				backgroundColor: colors.teal,
				borderWidth: 2,
				pointRadius: 3,
				tension: 0,
			});
			datasets.push({
				label: `Cash Flow: Buy Price for ${cashFlowReturnLabel}% Return`,
				data: makeSeries(cashFlowResult.buyPrice, cashFlowResult.futurePrice, cashFlowResult.years),
				borderColor: colors.teal,
				backgroundColor: colors.teal,
				borderWidth: 2,
				borderDash: [6, 6],
				pointRadius: 3,
				tension: 0,
			});
		}

		return { labels, datasets };
	}, [cashFlowResult, cashFlowReturnLabel, colors.blue, colors.teal, currentPrice, epsResult, epsReturnLabel]);

	const yAxisBounds = useMemo(() => {
		const values = chartData.datasets
			.flatMap((dataset: any) => (Array.isArray(dataset?.data) ? dataset.data : []))
			.map((value: unknown) => Number(value))
			.filter((value: number) => Number.isFinite(value));

		const maxValue = values.length > 0 ? Math.max(...values) : 0;
		const max = roundYAxisMax(maxValue);
		const stepSize = yAxisStepForMax(max);

		return { max, stepSize };
	}, [chartData.datasets]);

	const chartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: {
					display: false,
				},
			},
			scales: {
				x: {
					ticks: { color: colors.subText },
					grid: { color: colors.chartGrid },
				},
				y: {
					beginAtZero: true,
					min: 0,
					max: yAxisBounds.max,
					ticks: {
						color: colors.subText,
						stepSize: yAxisBounds.stepSize,
						includeBounds: true,
					},
					grid: { color: colors.chartGrid },
				},
			},
		}),
		[colors.chartGrid, colors.subText, yAxisBounds.max, yAxisBounds.stepSize]
	);

	const showChart = chartData.datasets.length > 0;

	const panelStyle: React.CSSProperties = {
		backgroundColor: colors.cardBg,
		border: `1px solid ${colors.border}`,
		borderRadius: 4,
		overflow: 'hidden',
	};

	const earningsPanelStyle: React.CSSProperties = {
		...panelStyle,
		border: 'none',
		width: 492,
		height: 605,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	};

	const cashflowPanelStyle: React.CSSProperties = {
		...panelStyle,
		border: 'none',
		width: 492,
		height: 605,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	};

	const panelHeaderStyle: React.CSSProperties = {
		height: 34,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '0 8px 0 7px',
		fontSize: 24,
		fontWeight: 500,
		color: colors.text,
		borderBottom: `1px solid ${colors.border}`,
		backgroundColor: colors.headerBg,
	};

	const rowStyle: React.CSSProperties = {
		width: 450,
		height: 52,
		display: 'grid',
		gridTemplateColumns: '1fr auto',
		alignItems: 'center',
		gap: 8,
		padding: '6px 10px',
		color: '#0D0D0D',
		fontFamily: 'Manrope, ui-sans-serif, system-ui',
		fontWeight: 400,
		fontStyle: 'normal',
		fontSize: 14,
		lineHeight: '100%',
		letterSpacing: '0',
		opacity: 1,
		boxSizing: 'border-box',
		borderBottom: `1px solid ${colors.border}`,
	};

	const inputWrapStyle: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
	};

	const inputStyle: React.CSSProperties = {
		width: 142,
		height: 40,
		border: '1px solid #D9D9D9',
		borderRadius: 8,
		backgroundColor: colors.inputBg,
		color: '#474D55',
		fontFamily: 'Manrope, ui-sans-serif, system-ui',
		fontWeight: 500,
		fontStyle: 'normal',
		fontSize: 14,
		lineHeight: '20px',
		letterSpacing: '0',
		textAlign: 'center',
		direction: 'ltr',
		paddingLeft: 0,
		paddingRight: 0,
		boxSizing: 'border-box',
		outline: 'none',
		MozAppearance: 'textfield',
	} as React.CSSProperties;

	const stepButtonStyle: React.CSSProperties = {
		border: 'none',
		background: 'transparent',
		color: colors.subText,
		width: 24,
		height: 24,
		padding: 0,
		cursor: 'pointer',
		lineHeight: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	};

	const outcomeChipStyle: React.CSSProperties = {
		width: 142,
		height: 40,
		borderRadius: 8,
		border: '1px solid #B3B3B3',
		backgroundColor: '#EDEDED',
		color: '#111827',
		fontSize: 12,
		fontWeight: 700,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		alignSelf: 'center',
		opacity: 1,
		boxSizing: 'border-box',
		padding: '0 8px',
	};

	const outcomeControlsWrapStyle: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		height: 40,
		gap: 5,
		transform: 'translateY(-5px)',
	};

	const valuationLegendBoxStyle: React.CSSProperties = {
		width: 660,
		height: 85,
		gap: 20,
		opacity: 1,
		border: '1px solid #D9D9D9',
		borderRadius: 6,
		padding: 10,
		boxSizing: 'border-box',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		margin: '0 auto 12px',
	};

	const legendColumnStyle: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: 280,
	};

	const legendHeadStyle = (color: string): React.CSSProperties => ({
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontFamily: 'Manrope, ui-sans-serif, system-ui',
		fontSize: 15,
		lineHeight: '20px',
		fontWeight: 500,
		color,
	});

	const legendLineRowStyle: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontFamily: 'Manrope, ui-sans-serif, system-ui',
		fontSize: 15,
		lineHeight: '20px',
		fontWeight: 400,
		color: '#262626',
	};

	const renderPanelHeader = (panelId: string, panelTitle: string) => {
		const iconSrc = panelId === 'valuation-earnings-panel' ? '/images/value_earning.svg' : '/images/value_cashflow.svg';
		return (
			<div
				id={`${panelId}-header-shell`}
				style={{
					...panelHeaderStyle,
					backgroundColor: '#ffffff',
					color: '#0D0D0D',
				}}
			>
				<div id={`${panelId}-header-title-wrap`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
					<img
						id={`${panelId}-title-icon`}
						src={iconSrc}
						alt=""
						aria-hidden="true"
						style={{ width: 24, height: 24, display: 'block' }}
					/>
					<span
						style={{
							fontFamily: 'Manrope, ui-sans-serif, system-ui',
							fontWeight: 500,
							fontStyle: 'normal',
							fontSize: 18,
							lineHeight: '100%',
							letterSpacing: '0.01em',
							color: '#0D0D0D',
						}}
					>
						{panelTitle}
					</span>
				</div>
				<button
					id={`${panelId}-bookmark-btn`}
					type="button"
					aria-label="Bookmark"
					title="Bookmark"
					style={{
						background: 'transparent',
						border: 'none',
						borderRadius: '8px',
						cursor: 'pointer',
						padding: 0,
						display: 'flex',
						alignItems: 'center',
						outline: 'none',
						boxShadow: 'none',
						opacity: savingBookmarkByPanel[panelId] ? 0.6 : 1,
						transition: 'background-color 120ms ease',
					}}
					disabled={savingBookmarkByPanel[panelId]}
					onClick={() => saveValuationBookmark(panelId, panelTitle)}
				>
					<img
						src={bookmarkFlashByPanel[panelId] ? '/images/bookmark_fill.svg' : (isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
						alt="Bookmark"
						style={{ width: 24, height: 24, display: 'block' }}
					/>
				</button>
			</div>
		);
	};

	const renderInputRow = (
		idPrefix: string,
		label: string,
		side: 'eps' | 'cashFlow',
		field: keyof SideInputs,
		opts?: {
			highlightRed?: boolean;
			rowStyleOverride?: React.CSSProperties;
			controlsStyleOverride?: React.CSSProperties;
		}
	) => {
		const controlKey = `${side}-${field}`;
		const isControlActive = activeControlKey === controlKey;

		return (
		<div id={`${idPrefix}-row`} style={{ ...rowStyle, ...(opts?.rowStyleOverride ?? {}) }}>
			<span style={{ display: 'inline-flex', alignItems: 'center', height: 40, transform: 'translateY(-5px)' }}>{label}</span>
			<div
				id={`${idPrefix}-controls-wrap`}
				style={{ ...inputWrapStyle, height: 40, alignItems: 'center', transform: 'translateY(-5px)', ...(opts?.controlsStyleOverride ?? {}) }}
			>
				<button
					id={`${idPrefix}-minus`}
					type="button"
					tabIndex={-1}
					style={{ ...stepButtonStyle, color: isControlActive ? '#0D0D0D' : '#D9D9D9' }}
					aria-label="Decrease value"
					onClick={() => stepField(side, field, -1)}
					onMouseEnter={() => setActiveControlKey(controlKey)}
					onMouseLeave={() => setActiveControlKey((prev) => (prev === controlKey ? null : prev))}
					onFocus={() => setActiveControlKey(controlKey)}
					onBlur={() => setActiveControlKey((prev) => (prev === controlKey ? null : prev))}
				>
					<span aria-hidden="true" style={{ fontSize: 24, lineHeight: '24px', fontWeight: 500, userSelect: 'none' }}>-</span>
				</button>
				<input
					id={`${idPrefix}-input`}
					style={{
						...inputStyle,
						color: opts?.highlightRed ? colors.red : inputStyle.color,
						fontWeight: opts?.highlightRed ? 800 : inputStyle.fontWeight,
					}}
					type="number"
					value={inputs[side][field]}
					onChange={(e) => setSideField(side, field, e.target.value)}
					onMouseEnter={() => setActiveControlKey(controlKey)}
					onMouseLeave={() => setActiveControlKey((prev) => (prev === controlKey ? null : prev))}
					onFocus={() => setActiveControlKey(controlKey)}
					onBlur={() => setActiveControlKey((prev) => (prev === controlKey ? null : prev))}
					placeholder={field === 'base' ? (side === 'eps' ? 'EPS ttm' : 'OCF/Share ttm') : 'Enter Number...'}
				/>
				<button
					id={`${idPrefix}-plus`}
					type="button"
					tabIndex={-1}
					style={{ ...stepButtonStyle, color: isControlActive ? '#0D0D0D' : '#D9D9D9' }}
					aria-label="Increase value"
					onClick={() => stepField(side, field, 1)}
					onMouseEnter={() => setActiveControlKey(controlKey)}
					onMouseLeave={() => setActiveControlKey((prev) => (prev === controlKey ? null : prev))}
					onFocus={() => setActiveControlKey(controlKey)}
					onBlur={() => setActiveControlKey((prev) => (prev === controlKey ? null : prev))}
				>
					<span aria-hidden="true" style={{ fontSize: 24, lineHeight: '24px', fontWeight: 500, userSelect: 'none' }}>+</span>
				</button>
			</div>
		</div>
		);
	};

	const renderOutcomeRow = (idPrefix: string, label: string, value: string) => (
		<div id={`${idPrefix}-row`} style={{ ...rowStyle }}>
			<span style={{ display: 'inline-flex', alignItems: 'center', height: 40, transform: 'translateY(-5px)', fontWeight: 700 }}>{label}</span>
			<div id={`${idPrefix}-controls-wrap`} style={outcomeControlsWrapStyle}>
				<span aria-hidden="true" style={{ width: 24, height: 24, display: 'inline-block' }} />
				<span id={`${idPrefix}-value`} style={outcomeChipStyle}>{value}</span>
				<span aria-hidden="true" style={{ width: 24, height: 24, display: 'inline-block' }} />
			</div>
		</div>
	);

	const renderLegendLineSample = (color: string, dashed?: boolean) => (
		<span
			aria-hidden="true"
			style={{
				width: 20,
				height: 0,
				borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
				display: 'inline-block',
			}}
		/>
	);

	return (
		<div
			id="valuation-screen"
			style={{
				minHeight: '100%',
				backgroundColor: '#ffffff',
				padding: '24px 24px 24px 74px',
				fontFamily: 'Manrope, ui-sans-serif, system-ui',
			}}
		>
			<div
				id="valuation-grid"
				style={{
					display: 'grid',
					gridTemplateColumns: '492px 700px 492px',
					width: '100%',
					columnGap: 'max(12px, calc((100% - 1760px) / 2))',
					alignItems: 'start',
				}}
			>
				<div id="valuation-earnings-panel" style={earningsPanelStyle}>
					<div id="valuation-earnings-header">{renderPanelHeader('valuation-earnings-panel', 'Earnings')}</div>
					{renderInputRow('valuation-eps-ttm', 'EPS ttm ($)', 'eps', 'base', {
						highlightRed: epsNeedsUpdateHighlight,
					})}
					{renderInputRow('valuation-eps-growth', 'Future Growth Rate (CAGR %)', 'eps', 'growthRate')}
					{renderInputRow('valuation-eps-pe', 'Normalized P/E', 'eps', 'multiple')}
					{renderInputRow('valuation-eps-years', 'Number of Years', 'eps', 'years')}
					{renderOutcomeRow('valuation-eps-future-price', 'Future Stock Price', fmtUsd(epsResult?.futurePrice))}
					{renderOutcomeRow('valuation-eps-cagr', 'Expected CAGR from Today\'s Stock Price', fmtPct(epsResult?.expectedCagrPct))}
					{renderInputRow('valuation-eps-min-return', 'Required Return (CAGR %)', 'eps', 'minimumReturn')}
					{renderOutcomeRow('valuation-eps-buy-price', `Buy Price For ${epsReturnLabel}% Return`, fmtUsd(epsResult?.buyPrice))}
					<div id="valuation-earnings-clear" style={{ padding: '8px 10px 12px', textAlign: 'right' }}>
						<button
							id="valuation-earnings-clear-btn"
							type="button"
							onClick={clearEarnings}
							style={{
								border: 'none',
								background: 'transparent',
								color: '#2F58F2',
								cursor: 'pointer',
								fontFamily: 'Manrope, ui-sans-serif, system-ui',
								fontWeight: 600,
								fontStyle: 'normal',
								fontSize: 14,
								lineHeight: '100%',
								letterSpacing: '0',
								verticalAlign: 'middle',
								transform: 'translateX(-70px)',
							}}
						>
							Clear All
						</button>
					</div>
				</div>

				<div
					style={{
						width: 700,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-start',
					}}
				>
					<div
						id="valuation-chart-panel"
						style={{
							...panelStyle,
							width: 700,
							height: 551,
							padding: '10px 20px',
							gap: 12,
							opacity: 1,
							border: '1px solid #D9D9D9',
							borderRadius: 6,
							boxSizing: 'border-box',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
					<div id="valuation-chart-header" style={{ ...panelHeaderStyle, backgroundColor: '#ffffff', borderBottom: 'none' }}>
						<div id="valuation-chart-title-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							<button
								type="button"
								aria-label="Expand chart"
								title="Expand chart"
								style={{
									background: 'transparent',
									border: 'none',
									cursor: 'pointer',
									padding: 0,
									display: 'flex',
									alignItems: 'center',
									outline: 'none',
									boxShadow: 'none',
								}}
								onClick={() => setExpandedChartId('valuation-chart-panel')}
							>
								<img src="/images/zoomin.svg" alt="" aria-hidden="true" style={{ width: 24, height: 24, display: 'block' }} />
							</button>
							<span style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui', fontWeight: 500, fontStyle: 'normal', fontSize: 16, lineHeight: '100%', letterSpacing: '0', color: '#0D0D0D' }}>Future Stock Price vs. Current &amp; Purchase Price</span>
						</div>
						<button
							id="valuation-chart-bookmark-btn"
							type="button"
							aria-label="Bookmark"
							title="Bookmark"
							style={{
								background: 'transparent',
								border: 'none',
								borderRadius: '8px',
								cursor: 'pointer',
								padding: 0,
								display: 'flex',
								alignItems: 'center',
								outline: 'none',
								boxShadow: 'none',
								opacity: savingBookmarkByPanel['valuation-chart-panel'] ? 0.6 : 1,
								transition: 'background-color 120ms ease',
							}}
							disabled={savingBookmarkByPanel['valuation-chart-panel']}
							onClick={() => saveValuationBookmark('valuation-chart-panel', 'Future Stock Price vs. Current & Purchase Price')}
						>
							<img
								src={bookmarkFlashByPanel['valuation-chart-panel'] ? '/images/bookmark_fill.svg' : (isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
								alt="Bookmark"
								style={{ width: 24, height: 24, display: 'block' }}
							/>
						</button>
					</div>
					<div id="valuation-chart-legend" style={valuationLegendBoxStyle}>
						<div style={legendColumnStyle}>
							<div style={legendHeadStyle(colors.blue)}>
								<span aria-hidden="true">●</span>
								<span>Earnings:</span>
							</div>
							<div style={legendLineRowStyle}>
								{renderLegendLineSample(colors.blue)}
								<span>Expected CAGR from Today&apos;s Price</span>
							</div>
							<div style={legendLineRowStyle}>
								{renderLegendLineSample(colors.blue, true)}
								<span>{`Buy Price for ${epsReturnLabel}% Return`}</span>
							</div>
						</div>
						<div style={legendColumnStyle}>
							<div style={legendHeadStyle(colors.teal)}>
								<span aria-hidden="true">●</span>
								<span>Cash Flow:</span>
							</div>
							<div style={legendLineRowStyle}>
								{renderLegendLineSample(colors.teal)}
								<span style={{ color: '#0D0D0D' }}>Expected CAGR from Today&#39;s Price</span>
							</div>
							<div style={legendLineRowStyle}>
								{renderLegendLineSample(colors.teal, true)}
								<span>{`Buy Price for ${cashFlowReturnLabel}% Return`}</span>
							</div>
						</div>
					</div>
					<div id="valuation-chart-body" style={{ flex: 1, minHeight: 0 }}>
						{showChart ? (
							<div id="valuation-chart-container" style={{ width: '100%', height: '100%' }}>
								<Line data={chartData} options={chartOptions} />
							</div>
						) : (
							<div
								id="valuation-chart-empty"
								style={{
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: colors.subText,
									fontSize: 12,
									textAlign: 'center',
								}}
							>
								Enter valuation inputs to show lines.
							</div>
						)}
					</div>
				</div>
				{valuationDataSource === 'fiscal' ? (
					<DataSourceAttribution isDarkMode={isDarkMode} style={{ marginTop: '0.75rem' }} />
				) : null}
				</div>

				<div id="valuation-cashflow-panel" style={cashflowPanelStyle}>
					<div id="valuation-cashflow-header">{renderPanelHeader('valuation-cashflow-panel', 'Cash Flow')}</div>
					{renderInputRow('valuation-cf-ttm', 'OCF/Share ttm ($)', 'cashFlow', 'base', {
						highlightRed: cashFlowNeedsUpdateHighlight,
					})}
					{renderInputRow('valuation-cf-growth', 'Future Growth Rate (CAGR %)', 'cashFlow', 'growthRate')}
					{renderInputRow('valuation-cf-pocf', 'Normalized P/OCF', 'cashFlow', 'multiple')}
					{renderInputRow('valuation-cf-years', 'Number of Years', 'cashFlow', 'years')}
					{renderOutcomeRow('valuation-cf-future-price', 'Future Stock Price', fmtUsd(cashFlowResult?.futurePrice))}
					{renderOutcomeRow('valuation-cf-cagr', 'Expected CAGR from Today\'s Stock Price', fmtPct(cashFlowResult?.expectedCagrPct))}
					{renderInputRow('valuation-cf-min-return', 'Required Return (CAGR %)', 'cashFlow', 'minimumReturn')}
					{renderOutcomeRow('valuation-cf-buy-price', `Buy Price For ${cashFlowReturnLabel}% Return`, fmtUsd(cashFlowResult?.buyPrice))}
					<div id="valuation-cashflow-clear" style={{ padding: '8px 10px 12px', textAlign: 'right' }}>
						<button
							id="valuation-cashflow-clear-btn"
							type="button"
							onClick={clearCashFlow}
							style={{
								border: 'none',
								background: 'transparent',
								color: '#2F58F2',
								cursor: 'pointer',
								fontFamily: 'Manrope, ui-sans-serif, system-ui',
								fontWeight: 600,
								fontStyle: 'normal',
								fontSize: 14,
								lineHeight: '100%',
								letterSpacing: '0',
								verticalAlign: 'middle',
								transform: 'translateX(-70px)',
							}}
						>
							Clear All
						</button>
					</div>
				</div>
			</div>

			{expandedChartId === 'valuation-chart-panel' && (
				<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 10000,
				}}
				onClick={() => setExpandedChartId(null)}
			>
				<div
					style={{
						backgroundColor: '#ffffff',
						borderRadius: 8,
						boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
						padding: '24px',
						width: '90%',
						maxWidth: '1200px',
						maxHeight: '90vh',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
					}}
					onClick={(e) => e.stopPropagation()}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '16px',
						}}
					>
						<h2
							style={{
								margin: 0,
								fontFamily: 'Manrope, ui-sans-serif, system-ui',
								fontSize: 20,
								fontWeight: 600,
								color: '#0D0D0D',
							}}
						>
							Future Stock Price vs. Current & Purchase Price
						</h2>
						<button
							type="button"
							aria-label="Close"
							style={{
								background: 'transparent',
								border: 'none',
								cursor: 'pointer',
								padding: 0,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: 32,
								height: 32,
								outline: 'none',
								boxShadow: 'none',
							}}
							onClick={() => setExpandedChartId(null)}
						>
							<span style={{ fontSize: 20, lineHeight: 1, color: '#0D0D0D' }}>×</span>
						</button>
					</div>
					<div
						style={{
							flex: 1,
							minHeight: 0,
							overflow: 'auto',
						}}
					>
						<div
							id="valuation-chart-legend"
							style={{
								...valuationLegendBoxStyle,
								marginBottom: '16px',
							}}
						>
							<div style={legendColumnStyle}>
								<div style={legendHeadStyle(colors.blue)}>
									<span aria-hidden="true">●</span>
									<span>Earnings:</span>
								</div>
								<div style={legendLineRowStyle}>
									{renderLegendLineSample(colors.blue)}
									<span>Expected CAGR from Today&apos;s Price</span>
								</div>
								<div style={legendLineRowStyle}>
									{renderLegendLineSample(colors.blue, true)}
									<span>{`Buy Price for ${epsReturnLabel}% Return`}</span>
								</div>
							</div>
							<div style={legendColumnStyle}>
								<div style={legendHeadStyle(colors.teal)}>
									<span aria-hidden="true">●</span>
									<span>Cash Flow:</span>
								</div>
								<div style={legendLineRowStyle}>
									{renderLegendLineSample(colors.teal)}
									<span style={{ color: '#0D0D0D' }}>Expected CAGR from Today&#39;s Price</span>
								</div>
								<div style={legendLineRowStyle}>
									{renderLegendLineSample(colors.teal, true)}
									<span>{`Buy Price for ${cashFlowReturnLabel}% Return`}</span>
								</div>
							</div>
						</div>
						<div
							style={{
								width: '100%',
								height: '500px',
							}}
						>
							{showChart ? (
								<Line data={chartData} options={chartOptions} />
							) : (
								<div
									style={{
										width: '100%',
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: colors.subText,
										fontSize: 12,
										textAlign: 'center',
									}}
								>
									Enter valuation inputs to show lines.
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		)}
	</div>
	);
};

export default ResearchValuationScreen;
